import Box from '@/Atoms/Box';
import CCText from '@/Atoms/CCText';
import React, {useEffect, useState} from 'react';
import AntDesign from 'react-native-vector-icons/AntDesign';
import {styles} from './styles';
import useManageParticipants from '@/Hooks/ManageStudyParticipantsApi/useManageParticipants';
import useUpdateParticipantRole from '@/Hooks/ManageStudyParticipantsApi/useUpdateParticipantRole';
import Addedparticipants from '../AddParticipants';
import CCColors, {updateColorWithOpacity} from '@/Utils/CCColors';
import {LeftComponentType} from './types';
import Spacer from '@/Atoms/Spacer';
import {ScrollView, TouchableOpacity} from 'react-native';
import {useToast} from '@/toast/useToast';

// Small hoverable icon button - same pattern as Chapters' ChapterActionButton,
// gives the delete action a real hit target + hover tint instead of a bare
// icon glyph floating on the row.
function ParticipantActionButton({
  iconName,
  color,
  hoverColor,
  hoverBackground,
  onPress,
}: {
  iconName: string;
  color: string;
  hoverColor: string;
  hoverBackground: string;
  onPress: (e: any) => void;
}) {
  const [isHovered, setIsHovered] = useState(false);
  return (
    <TouchableOpacity
      onHoverIn={() => setIsHovered(true)}
      onHoverOut={() => setIsHovered(false)}
      onPress={onPress}
      style={[
        styles.actionButton,
        isHovered && {backgroundColor: hoverBackground},
      ]}>
      <AntDesign
        name={iconName}
        size={14}
        color={isHovered ? hoverColor : color}
      />
    </TouchableOpacity>
  );
}

const AVATAR_COLORS = [
  CCColors.Green,
  CCColors.Workbook.Orange,
  CCColors.Workbook.Brown,
  CCColors.Workbook.Purple,
  CCColors.Workbook.BlueAccent,
];

function avatarColorFor(seed: string) {
  const code = seed ? seed.charCodeAt(0) : 0;
  return AVATAR_COLORS[code % AVATAR_COLORS.length];
}

type ParticipantRowProps = {
  player: any;
  isYou: boolean;
  isSelected: boolean;
  isOwner: boolean;
  onSelect: () => void;
  onRoleChange: (role: 'contributor' | 'viewer') => void;
  onDelete: (e: any) => void;
};

// Extracted so each row owns its own hover state, matching Chapters' row
// pattern - `.map()` callbacks can't call hooks directly.
function ParticipantRow({
  player,
  isYou,
  isSelected,
  isOwner,
  onSelect,
  onRoleChange,
  onDelete,
}: ParticipantRowProps) {
  const [isHovered, setIsHovered] = useState(false);
  const initial = (player?.username?.trim()?.charAt(0) || '?').toUpperCase();
  const isOwnerRow = !player?.isDeletable;
  const canDelete = player?.isDeletable && isSelected && isOwner;

  return (
    <Box
      style={[
        styles.participantsName,
        isSelected ? styles.selectedName : {},
        isHovered && !isSelected && styles.hoveredName,
      ]}
      onHoverIn={() => setIsHovered(true)}
      onHoverOut={() => setIsHovered(false)}
      onPress={onSelect}>
      <Box style={styles.rowLeading}>
        <Box
          style={[
            styles.avatarCircle,
            {
              backgroundColor: isYou
                ? undefined
                : updateColorWithOpacity(
                    avatarColorFor(player?.username),
                    0.15,
                  ),
            },
            isYou && styles.avatarCircleYou,
          ]}>
          <CCText
            style={[
              styles.avatarText,
              {color: avatarColorFor(player?.username)},
              isYou && styles.avatarTextYou,
            ]}>
            {initial}
          </CCText>
        </Box>
        <Box style={{flex: 1}}>
          <Box style={{flexDirection: 'row', alignItems: 'center', gap: 6}}>
            <CCText
              fontSize={13}
              fontWeight={isSelected ? 'medium' : 'regular'}
              color={
                isSelected
                  ? CCColors.TextColor.Dark
                  : CCColors.TextColor.Primary
              }
              numberOfLines={1}>
              {player?.username}
            </CCText>
            {isYou && (
              <CCText fontSize={10} color={CCColors.Workbook.Grey}>
                (you)
              </CCText>
            )}
          </Box>
          {/* Owner/role indicators simplified to plain text (or, for the
              owner's own editing control, a compact segmented toggle) -
              dropped the boxed/bordered badges each option used to get,
              which read as more chrome than a one-word status needs. */}
          {isOwnerRow ? (
            <CCText
              fontSize={10}
              fontWeight="semibold"
              color={CCColors.Workbook.GoldText}
              style={{marginTop: 2}}>
              Owner
            </CCText>
          ) : isOwner ? (
            <Box style={[styles.roleToggleContainer, {marginTop: 4}]}>
              <Box
                style={[
                  styles.roleTogglePill,
                  (player?.role ?? 'contributor') === 'contributor' &&
                    styles.roleTogglePillActive,
                ]}
                onPress={(e: any) => {
                  e.stopPropagation();
                  onRoleChange('contributor');
                }}>
                <CCText
                  fontSize={9}
                  fontWeight="bold"
                  color={
                    (player?.role ?? 'contributor') === 'contributor'
                      ? CCColors.TextColor.Dark
                      : CCColors.Workbook.BrownGrey
                  }>
                  Contributor
                </CCText>
              </Box>
              <Box
                style={[
                  styles.roleTogglePill,
                  player?.role === 'viewer' && styles.roleTogglePillActive,
                ]}
                onPress={(e: any) => {
                  e.stopPropagation();
                  onRoleChange('viewer');
                }}>
                <CCText
                  fontSize={9}
                  fontWeight="bold"
                  color={
                    player?.role === 'viewer'
                      ? CCColors.TextColor.Dark
                      : CCColors.Workbook.BrownGrey
                  }>
                  Viewer
                </CCText>
              </Box>
            </Box>
          ) : (
            <CCText
              fontSize={10}
              color={CCColors.Workbook.BrownGrey}
              style={{marginTop: 2}}>
              {player?.role === 'viewer' ? 'Viewer' : 'Contributor'}
            </CCText>
          )}
        </Box>
      </Box>
      {canDelete && (
        <ParticipantActionButton
          iconName="delete"
          color={CCColors.Error}
          hoverColor={CCColors.Error}
          hoverBackground={updateColorWithOpacity(
            CCColors.Workbook.DangerRed,
            0.12,
          )}
          onPress={onDelete}
        />
      )}
    </Box>
  );
}

function Participants({
  participantsList,
  userDetailsData,
  gameId,
  setLoading,
  refetchStudy,
  isOwner,
  selectedUser,
  setSelectedUser,
  setOpenRemoveConfimation,
}: LeftComponentType) {
  const showToast = useToast();

  const handleParticipantsSuccess = data => {
    setLoading?.(false);
    refetchStudy?.();
    showToast('Participant updated successfully', {type: 'Success'});
  };

  const handleParticipantsError = data => {
    setLoading?.(false);
    showToast('Failed to update participant', {type: 'Error'});
  };
  const {mutate: manageParticipants, isLoading: isUpdatingPlayers} =
    useManageParticipants(handleParticipantsSuccess, handleParticipantsError);

  const {mutate: updateParticipantRole} = useUpdateParticipantRole(
    () => {
      refetchStudy?.();
      showToast('Role updated successfully', {type: 'Success'});
    },
    () => showToast('Failed to update role', {type: 'Error'}),
  );

  const handleParticipants = (
    phone: string,
    action: 'add' | 'remove' = 'add',
  ) => {
    setLoading?.(true);
    manageParticipants({
      phone: phone,
      action: action,
      gameId: gameId,
    });
  };

  const handleRoleChange = (phone: string, role: 'contributor' | 'viewer') => {
    updateParticipantRole({gameId, phone, role});
  };

  return (
    <>
      {/* <CCText fontWeight="semibold">Participants</CCText> */}
      <Spacer spacing={10} />
      <ScrollView
        style={styles.scrollContainer}
        contentContainerStyle={{paddingBottom: 20}}
        showsVerticalScrollIndicator={false}>
        <Box>
          {participantsList?.map(player => {
            const playerId = player?.mobile_number || player?.username;
            const isSelected = selectedUser?.username === player?.username;
            const isYou =
              player?.mobile_number === userDetailsData?.mobile_number;
            return (
              <ParticipantRow
                key={playerId}
                player={player}
                isYou={isYou}
                isSelected={isSelected}
                isOwner={isOwner}
                onSelect={() => setSelectedUser(player)}
                onRoleChange={role =>
                  handleRoleChange(player?.mobile_number, role)
                }
                onDelete={(e: any) => {
                  e.stopPropagation();
                  setOpenRemoveConfimation({
                    flag: true,
                    title: `Remove ${
                      player?.username || player?.mobile_number
                    }?`,
                    bodyTxt:
                      'They leave the board immediately — you can invite them back anytime.',
                    piece: 'chess-pawn',
                    confirmLabel: 'Remove Player',
                    callbackFunc: async () =>
                      await handleParticipants(player?.mobile_number, 'remove'),
                  });
                }}
              />
            );
          })}
        </Box>
      </ScrollView>
      <Addedparticipants
        gameId={gameId}
        setLoading={setLoading}
        refetchStudy={refetchStudy}
        isOwner={isOwner}
      />
    </>
  );
}

export default Participants;
